import { useRef } from "react";
import { useWorkshopLocations, type WorkshopLocation } from "../hooks/useWorkshopLocations";
import { LocationsMap, type LocationsMapHandle } from "./LocationsMap";

function LocationCard({
  loc,
  onSelect,
}: {
  loc: WorkshopLocation;
  onSelect: (id: string) => void;
}) {
  const handleClick = () => onSelect(loc.id);
  const handleKey = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(loc.id);
    }
  };
  return (
    <article
      className="loc-card loc-card-clickable"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKey}
      aria-label={`Toon ${loc.naam} op de kaart`}
    >
      <div className="loc-card-body">
        <div className="loc-card-tag">{loc.stad || "Vlaanderen"}</div>
        <h3>{loc.naam}</h3>
        {loc.adres && <p className="loc-card-adres">{loc.adres}</p>}
        {loc.typeRuimte && (
          <ul className="loc-card-meta">
            <li>{loc.typeRuimte}</li>
          </ul>
        )}
        <span className="loc-card-cta">Bekijk op kaart →</span>
      </div>
    </article>
  );
}

function Skeleton() {
  return (
    <>
      <div className="loc-map loc-map-skeleton" />
      <div className="loc-grid">
        {[0, 1].map((i) => (
          <div key={i} className="loc-card loc-card-skeleton" />
        ))}
      </div>
    </>
  );
}

export function WorkshopLocations() {
  const state = useWorkshopLocations();
  const locations = state.status === "ok" ? state.locations : [];
  const mapRef = useRef<LocationsMapHandle | null>(null);

  const handleSelect = (id: string) => {
    mapRef.current?.focusLocation(id);
  };

  return (
    <section className="bg-alt" id="locaties">
      <div className="container">
        <div className="section-head">
          <span className="section-tag">Locaties</span>
          <h2>Waar vinden de workshops plaats?</h2>
          <p>Kleine, vertrouwde plekken in jouw buurt. Klik een kaart of marker voor de details.</p>
        </div>

        {state.status === "loading" && <Skeleton />}
        {state.status === "error" && (
          <p className="upcoming-empty">Locaties zijn even niet beschikbaar.</p>
        )}
        {state.status === "ok" && locations.length === 0 && (
          <p className="upcoming-empty">Nog geen locaties geregistreerd.</p>
        )}
        {state.status === "ok" && locations.length > 0 && (
          <>
            <LocationsMap ref={mapRef} locations={locations} />
            <div className="loc-grid">
              {locations.map((loc) => (
                <LocationCard key={loc.id} loc={loc} onSelect={handleSelect} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
